import { cloneRules } from "./kerioAst.js";
import { ACTION_TYPES, CONDITION_TARGETS, SIZE_COMPARATORS, TEXT_COMPARATORS, actionTypeLabel, comparatorLabel, conditionTargetLabel, defaultComparatorForTarget, describeKerioRule, KERIO_PALETTE, } from "./kerioRegistry.js";
import { el } from "../dom.js";
function paramCountForAction(type) {
    switch (type) {
        case "FaAddHeader":
        case "FaSetHeader":
            return 2;
        case "FaNotify":
            return 3;
        case "FaRemoveHeader":
        case "FaAddRecipient":
        case "FaCopyToAddress":
        case "FaReject":
        case "FaFileInto":
        case "FaRedirect":
        case "FaAutoReply":
            return 1;
        default:
            return 0;
    }
}
function needsValueParams(target) {
    return !(target === "CtAttachment" || target === "CtSpam" || target === "CtAll");
}
export function renderKerioBuilderUi(options) {
    const root = el("div", "kerio-builder");
    const paletteCol = el("div", "sieve-palette-column");
    paletteCol.append(el("h4", undefined, "Palette"));
    const paletteList = el("div", "sieve-palette");
    for (const item of KERIO_PALETTE) {
        const btn = el("button", "btn btn-secondary btn-sm sieve-palette-item", item.label);
        btn.type = "button";
        btn.addEventListener("click", () => {
            const next = cloneRules(options.rules);
            next.push(item.createRule());
            options.onChange(next, next.length - 1);
        });
        paletteList.append(btn);
    }
    paletteCol.append(paletteList);
    const rulesCol = el("div", "sieve-rules-column");
    rulesCol.append(el("h4", undefined, "Rules"));
    const ruleList = el("ul", "sieve-script-list kerio-rule-list");
    options.rules.forEach((rule, index) => {
        const li = el("li", options.selectedIndex === index ? "active" : undefined);
        const enabled = el("input");
        enabled.type = "checkbox";
        enabled.checked = rule.isEnabled;
        enabled.title = "Enabled";
        enabled.addEventListener("click", (ev) => ev.stopPropagation());
        enabled.addEventListener("change", () => {
            const next = cloneRules(options.rules);
            next[index] = { ...next[index], isEnabled: enabled.checked };
            options.onChange(next, options.selectedIndex);
        });
        const label = el("span", "kerio-rule-summary", rule.description || describeKerioRule(rule));
        li.append(enabled, label);
        li.addEventListener("click", () => options.onChange(options.rules, index));
        ruleList.append(li);
    });
    rulesCol.append(ruleList);
    const editorCol = el("div", "sieve-editor-column");
    editorCol.append(el("h4", undefined, "Rule editor"));
    if (options.selectedIndex === null || !options.rules[options.selectedIndex]) {
        editorCol.append(el("p", "muted", "Select a rule or add one from the palette."));
    }
    else {
        const idx = options.selectedIndex;
        const rule = options.rules[idx];
        const updateRule = (patch) => {
            const next = cloneRules(options.rules);
            next[idx] = { ...next[idx], ...patch };
            options.onChange(next, idx);
        };
        const descLabel = el("label", undefined, "Description");
        const descInput = el("input", "input");
        descInput.value = rule.description;
        descInput.addEventListener("change", () => updateRule({ description: descInput.value }));
        editorCol.append(descLabel, descInput);
        const modeRow = el("div", "inline-add mt");
        modeRow.append(el("span", undefined, "Match "));
        const modeSelect = el("select", "input");
        for (const [value, label] of [
            ["EmAllOf", "all conditions"],
            ["EmAnyOf", "any condition"],
        ]) {
            const opt = el("option", undefined, label);
            opt.value = value;
            if (rule.evaluationMode === value)
                opt.selected = true;
            modeSelect.append(opt);
        }
        modeSelect.addEventListener("change", () => updateRule({
            evaluationMode: modeSelect.value,
        }));
        modeRow.append(modeSelect);
        editorCol.append(modeRow);
        editorCol.append(el("h4", "mt", "Conditions"));
        const condList = el("div", "kerio-condition-list");
        rule.conditions.forEach((cond, cIndex) => {
            condList.append(renderConditionEditor(cond, (nextCond) => {
                const conditions = [...rule.conditions];
                conditions[cIndex] = nextCond;
                updateRule({ conditions });
            }, () => {
                const conditions = rule.conditions.filter((_, i) => i !== cIndex);
                updateRule({ conditions });
            }));
        });
        const addCond = el("button", "btn btn-secondary btn-sm mt", "Add condition");
        addCond.type = "button";
        addCond.addEventListener("click", () => {
            updateRule({
                conditions: [
                    ...rule.conditions,
                    {
                        testedTarget: "CtSubject",
                        comparator: "CcContain",
                        parameters: [""],
                    },
                ],
            });
        });
        editorCol.append(condList, addCond);
        editorCol.append(el("h4", "mt", "Actions"));
        const actList = el("div", "kerio-action-list");
        rule.actions.forEach((act, aIndex) => {
            actList.append(renderActionEditor(act, (nextAct) => {
                const actions = [...rule.actions];
                actions[aIndex] = nextAct;
                updateRule({ actions });
            }, () => {
                const actions = rule.actions.filter((_, i) => i !== aIndex);
                updateRule({ actions });
            }));
        });
        const addAct = el("button", "btn btn-secondary btn-sm mt", "Add action");
        addAct.type = "button";
        addAct.addEventListener("click", () => {
            updateRule({
                actions: [...rule.actions, { type: "FaKeep", parameters: [] }],
            });
        });
        editorCol.append(actList, addAct);
        const removeRule = el("button", "btn btn-secondary btn-sm mt", "Delete rule");
        removeRule.type = "button";
        removeRule.addEventListener("click", () => {
            const next = options.rules.filter((_, i) => i !== idx);
            options.onChange(next, null);
        });
        editorCol.append(removeRule);
    }
    root.append(paletteCol, rulesCol, editorCol);
    return root;
}
function renderConditionEditor(cond, onChange, onRemove) {
    const row = el("div", "kerio-edit-row card-like mt");
    const targetSelect = el("select", "input");
    for (const t of CONDITION_TARGETS) {
        const opt = el("option", undefined, conditionTargetLabel(t));
        opt.value = t;
        if (cond.testedTarget === t)
            opt.selected = true;
        targetSelect.append(opt);
    }
    targetSelect.addEventListener("change", () => {
        const testedTarget = targetSelect.value;
        onChange({
            testedTarget,
            comparator: defaultComparatorForTarget(testedTarget),
            parameters: needsValueParams(testedTarget) ? [cond.parameters[0] ?? ""] : [],
        });
    });
    row.append(targetSelect);
    if (needsValueParams(cond.testedTarget)) {
        const comps = cond.testedTarget === "CtSize" ? SIZE_COMPARATORS : TEXT_COMPARATORS;
        const compSelect = el("select", "input");
        for (const c of comps) {
            const opt = el("option", undefined, comparatorLabel(c));
            opt.value = c;
            if (cond.comparator === c)
                opt.selected = true;
            compSelect.append(opt);
        }
        compSelect.addEventListener("change", () => onChange({
            ...cond,
            comparator: compSelect.value,
        }));
        const valueInput = el("input", "input");
        valueInput.value = cond.parameters[0] ?? "";
        valueInput.placeholder =
            cond.testedTarget === "CtSize" ? "bytes" : "value";
        valueInput.addEventListener("change", () => onChange({ ...cond, parameters: [valueInput.value] }));
        row.append(compSelect, valueInput);
    }
    const remove = el("button", "btn btn-secondary btn-sm", "Remove");
    remove.type = "button";
    remove.addEventListener("click", onRemove);
    row.append(remove);
    return row;
}
function renderActionEditor(act, onChange, onRemove) {
    const row = el("div", "kerio-edit-row card-like mt");
    const typeSelect = el("select", "input");
    for (const t of ACTION_TYPES) {
        const opt = el("option", undefined, actionTypeLabel(t));
        opt.value = t;
        if (act.type === t)
            opt.selected = true;
        typeSelect.append(opt);
    }
    typeSelect.addEventListener("change", () => {
        const type = typeSelect.value;
        const count = paramCountForAction(type);
        onChange({
            type,
            parameters: Array.from({ length: count }, (_, i) => act.parameters[i] ?? ""),
        });
    });
    row.append(typeSelect);
    const count = paramCountForAction(act.type);
    const placeholders = act.type === "FaNotify"
        ? ["address", "subject", "message"]
        : act.type === "FaAddHeader" || act.type === "FaSetHeader"
            ? ["header name", "header value"]
            : act.type === "FaFileInto"
                ? ["folder path"]
                : act.type === "FaAutoReply"
                    ? ["message"]
                    : ["value"];
    for (let i = 0; i < count; i++) {
        const input = el("input", "input");
        input.value = act.parameters[i] ?? "";
        input.placeholder = placeholders[i] ?? `param ${i + 1}`;
        const index = i;
        input.addEventListener("change", () => {
            const parameters = [...act.parameters];
            while (parameters.length < count)
                parameters.push("");
            parameters[index] = input.value;
            onChange({ ...act, parameters });
        });
        row.append(input);
    }
    const remove = el("button", "btn btn-secondary btn-sm", "Remove");
    remove.type = "button";
    remove.addEventListener("click", onRemove);
    row.append(remove);
    return row;
}
